import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  message,
  Card,
  Rate,
  InputNumber
} from 'antd';
import apiService from "services/api/apiService";
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

interface PerformanceReviewFormProps {
  review?: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const PerformanceReviewForm: React.FC<PerformanceReviewFormProps> = ({ review, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);

  useEffect(() => {
    fetchEmployees();
    if (review) {
      form.setFieldsValue({
        ...review,
        review_date: review.review_date ? dayjs(review.review_date) : null,
      });
    }
  }, [review, form]);

  const fetchEmployees = async () => {
  try {
    const response = await apiService.getEmployees();
    if (response.success) {
      let employeeData = response.data || [];
      
      // Ensure it's always an array
      if (!Array.isArray(employeeData)) {
        if (employeeData.employees) {
          employeeData = employeeData.employees;
        } else if (employeeData.data) {
          employeeData = employeeData.data;
        } else {
          employeeData = [];
        }
      }
      
      setEmployees(Array.isArray(employeeData) ? employeeData : []);
    } else {
      setEmployees([]);
    }
  } catch (error) {
    console.error('Employee fetch error:', error);
    message.error('Failed to fetch employees');
    setEmployees([]);
  }
};

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      const reviewData = {
        employee_id: values.employee_id,
        review_period: values.review_period,
        overall_rating: values.overall_rating || 0,
        goals_achievement: values.goals_achievement || 0,
        teamwork: values.teamwork || 0,
        communication: values.communication || 0,
        technical_skills: values.technical_skills || 0,
        punctuality: values.punctuality || 0,
        leadership: values.leadership || 0,
        problem_solving: values.problem_solving || 0,
        comments: values.comments || '',
        achievements: values.achievements || '',
        areas_for_improvement: values.areas_for_improvement || '',
        goals_next_period: values.goals_next_period || '',
        reviewer_name: values.reviewer_name,
        review_date: values.review_date ? values.review_date.format('YYYY-MM-DD') : null
      };

      let response;
      if (review) {
        response = await apiService.updatePerformanceReview(review.id, reviewData);
      } else {
        response = await apiService.createPerformanceReview(reviewData);
      }

      if (response.success) {
        message.success(review ? 'Performance review updated successfully' : 'Performance review created successfully');
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.error || 'Operation failed');
      }
    } catch (error: any) {
      console.error('Performance review operation error:', error);
      message.error(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        overall_rating: 0,
        goals_achievement: 0,
        teamwork: 0,
        communication: 0,
        technical_skills: 0,
        punctuality: 0,
        leadership: 0,
        problem_solving: 0
      }}
    >
      <Card title="Basic Information" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="employee_id"
              label="Employee"
              rules={[{ required: true, message: 'Please select an employee' }]}
            >
              <Select
                placeholder="Select employee"
                showSearch
                optionFilterProp="children"
                disabled={!!review}
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} - {emp.department}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="review_period"
              label="Review Period"
              rules={[{ required: true, message: 'Please enter review period' }]}
            >
              <Input placeholder="e.g., Q4 2025, Annual 2025" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="reviewer_name"
              label="Reviewer Name"
              rules={[{ required: true, message: 'Please enter reviewer name' }]}
            >
              <Input placeholder="Enter reviewer name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="review_date"
              label="Review Date"
              rules={[{ required: true, message: 'Please select review date' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Select review date" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Performance Ratings (0-5 Scale)" style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="overall_rating" label="Overall Rating">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="goals_achievement" label="Goals Achievement">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="teamwork" label="Teamwork">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="communication" label="Communication">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="technical_skills" label="Technical Skills">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="punctuality" label="Punctuality">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="leadership" label="Leadership">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="problem_solving" label="Problem Solving">
              <Rate allowHalf count={5} />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Comments & Feedback">
        <Form.Item name="achievements" label="Key Achievements">
          <TextArea rows={3} placeholder="List key achievements during this period" />
        </Form.Item>

        <Form.Item name="areas_for_improvement" label="Areas for Improvement">
          <TextArea rows={3} placeholder="Areas where employee can improve" />
        </Form.Item>

        <Form.Item name="goals_next_period" label="Goals for Next Period">
          <TextArea rows={3} placeholder="Goals and objectives for the next review period" />
        </Form.Item>

        <Form.Item name="comments" label="Additional Comments">
          <TextArea rows={3} placeholder="Any additional comments or feedback" />
        </Form.Item>
      </Card>

      <Form.Item style={{ marginTop: '24px' }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            {review ? 'Update Review' : 'Create Review'}
          </Button>
          <Button onClick={onCancel}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default PerformanceReviewForm;
